// routeDrawing.js — pure helpers for player long-press route drawing.
//
// Mirrors the per-step rules findPath uses (in pathfinding.js): Chebyshev
// neighbors, diagonal corner check, wall edges, footprint passability,
// difficult-cell cost. Pulled out of TokenLayer so the state machine stays
// readable and the rules can be unit-tested in isolation.

import { wallBlocksStep } from './pathfinding';

/**
 * Decide whether the route can extend from `prev` to `next`.
 *
 * @param {{x: number, y: number}} prev      Last route cell (or token anchor).
 * @param {{x: number, y: number}} next      Candidate cell.
 * @param {Object} ctx
 *   - cells, gridW, gridH    map data
 *   - size                   token footprint side length
 *   - allEdges               wall + closed-door edge set
 *   - wallBlockedCells       cells walls physically pass through
 *   - blockedCells           hard-blocked cells (enemy footprints)
 *   - difficultCells         soft-blocked cells (count as difficult)
 *   - moverProne, isFlying   per-mover modifiers
 * @returns {{ok: true, difficult: boolean, cost: number} | {ok: false}}
 */
export function canAppendCell(prev, next, ctx) {
  if (!prev || !next) return { ok: false };

  const dx = next.x - prev.x;
  const dy = next.y - prev.y;
  const adx = Math.abs(dx), ady = Math.abs(dy);

  // Must be a Chebyshev neighbor (cardinal or single diagonal step).
  if (adx > 1 || ady > 1 || (adx === 0 && ady === 0)) return { ok: false };

  const { cells, gridW, gridH, size, allEdges, wallBlockedCells,
          blockedCells, difficultCells, moverProne, isFlying } = ctx;

  // Bounds + footprint passability.
  if (next.x < 0 || next.y < 0 || next.x + size > gridW || next.y + size > gridH) {
    return { ok: false };
  }
  for (let fx = 0; fx < size; fx++) {
    for (let fy = 0; fy < size; fy++) {
      const idx  = (next.y + fy) * gridW + (next.x + fx);
      const cell = cells[idx];
      if (!cell || cell.terrain === 'empty' || !cell.passable) return { ok: false };
      const k = `${next.x + fx},${next.y + fy}`;
      if (blockedCells?.has(k)) return { ok: false };
      if (wallBlockedCells?.has(k)) return { ok: false };
    }
  }

  // Wall / diagonal corner check — mirrors findPath. Flying skips walls.
  if (!isFlying) {
    const diag = adx === 1 && ady === 1;
    if (diag) {
      // Both cardinals must be passable AND no wall on either cardinal step.
      const sideA = { x: next.x, y: prev.y };
      const sideB = { x: prev.x, y: next.y };
      if (!footprintClear(sideA, size, cells, gridW, gridH)) return { ok: false };
      if (!footprintClear(sideB, size, cells, gridW, gridH)) return { ok: false };
      if (wallBlocksStep(prev.x, prev.y, sideA.x, sideA.y, size, allEdges)) return { ok: false };
      if (wallBlocksStep(prev.x, prev.y, sideB.x, sideB.y, size, allEdges)) return { ok: false };
    } else {
      if (wallBlocksStep(prev.x, prev.y, next.x, next.y, size, allEdges)) return { ok: false };
    }
  }

  // Entry cost — 2 for difficult terrain / soft-blocked (creature space).
  let difficult = false;
  if (!isFlying) {
    for (let fx = 0; fx < size && !difficult; fx++) {
      for (let fy = 0; fy < size && !difficult; fy++) {
        const cx = next.x + fx, cy = next.y + fy;
        if (difficultCells?.has(`${cx},${cy}`)) difficult = true;
        else if (cells[cy * gridW + cx]?.difficult) difficult = true;
      }
    }
    if (moverProne) difficult = true;
  }

  return { ok: true, difficult, cost: difficult ? 2 : 1 };
}

function footprintClear(corner, size, cells, gridW, gridH) {
  if (corner.x < 0 || corner.y < 0 || corner.x + size > gridW || corner.y + size > gridH) {
    return false;
  }
  for (let fx = 0; fx < size; fx++) {
    for (let fy = 0; fy < size; fy++) {
      const c = cells[(corner.y + fy) * gridW + (corner.x + fx)];
      if (!c || c.terrain === 'empty' || !c.passable) return false;
    }
  }
  return true;
}

export function routeTotalCost(route) {
  let sum = 0;
  for (const c of route || []) sum += c.cost || 1;
  return sum;
}
